import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Archive, Plus, Pencil, Trash2, Upload, Paperclip, ClipboardPaste, FileDown, Search, X, ArrowUpDown, ChevronRight, FileArchive, FileSignature } from 'lucide-react';
import { useData } from '../App';
import { CounterpartyType, Counterparty, Contract, CONTRACT_KINDS, CpKind, CP_KIND_LABELS, cpKind, SelfEmployedStatus } from '../types';
import { Button, Card, Input, Modal, Badge, Select } from '../components/UI';
import { api } from '../services/api';
import { formatDay, formatMoney, orgDisplayName } from '../utils/format';
import { ContractForm, downloadContract } from '../components/billing/Setup';
import { NpdStatus, SEContractForm, SelfEmployedWorks } from '../components/directory/SelfEmployed';
import { CrmNewEmployees } from '../components/directory/CrmNewEmployees';
import { StaffSections } from '../components/directory/Staff';
import { EdoArchiveModal, EdoNewContractsModal } from '../components/edo/Edo';
import { confirmDialog } from '../components/Dialogs';

type Tab = 'cp' | 'staff' | 'se' | 'archive';
type SortKey = 'name' | 'kind' | 'inn' | 'phone' | 'debt' | 'last';
type Summary = Record<string, { debtToUs: number; ourDebt: number; lastOperation: string | null }>;

const KIND_TO_TYPE: Record<CpKind, [CounterpartyType, boolean]> = {
  LEGAL: [CounterpartyType.LEGAL_ENTITY, false], IP: [CounterpartyType.ENTREPRENEUR, false], PERSON: [CounterpartyType.INDIVIDUAL, false],
  SELF_EMPLOYED: [CounterpartyType.INDIVIDUAL, true], STAFF: [CounterpartyType.STAFF, false],
};
const TAB_KINDS: Record<Tab, CpKind[]> = { cp: ['LEGAL', 'IP', 'PERSON'], staff: ['STAFF'], se: ['SELF_EMPLOYED'],
                                          archive: ['LEGAL', 'IP', 'PERSON', 'STAFF', 'SELF_EMPLOYED'] };
const KIND_BADGE: Record<CpKind, 'neutral' | 'success' | 'warning'> = { LEGAL: 'neutral', IP: 'neutral', PERSON: 'neutral', SELF_EMPLOYED: 'warning', STAFF: 'success' };

const DebtCell: React.FC<{ s?: Summary[string] }> = ({ s }) => (
  <span className="whitespace-nowrap font-mono text-xs">
    {s && s.debtToUs > 0.004 && <span className="text-red-600 font-semibold" title="Долг нам">{formatMoney(s.debtToUs)}</span>}
    {s && s.ourDebt > 0.004 && <span className="text-amber-700 font-semibold block" title="Наш долг">−{formatMoney(s.ourDebt)}</span>}
    {(!s || (s.debtToUs <= 0.004 && s.ourDebt <= 0.004)) && <span className="text-slate-300">—</span>}
  </span>
);

export const Directories: React.FC = () => {
  const { state, addCounterparty, updateCounterparty, deleteCounterparty, refreshData, showNotification, revision } = useData();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [tab, setTab] = useState<Tab>('cp');
  const [edoModal, setEdoModal] = useState<'contracts' | 'archive' | null>(null);  // ЭДО с самозанятыми
  const [kindFilter, setKindFilter] = useState<'' | CpKind>('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'name', dir: 1 });
  const [summary, setSummary] = useState<Summary>({});
  // ?open=<id> — открыть карточку (ссылки с дашборда: топ получателей)
  const [openId, setOpenId] = useState<string | null>(() => new URLSearchParams(window.location.search).get('open'));
  const [seContractFor, setSeContractFor] = useState<Counterparty | null>(null);

  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { api.counterpartiesSummary().then(setSummary).catch(() => setSummary({})); }, [revision, state.counterparties]);

  // --- STATE ФОРМЫ ---
  const [kind, setKind] = useState<CpKind>('LEGAL');
  const [type, isSelfEmployed] = KIND_TO_TYPE[kind];
  const [name, setName] = useState('');
  const [contractNumber, setContractNumber] = useState('');
  const [contractDate, setContractDate] = useState('');
  const [inn, setInn] = useState('');
  const [kpp, setKpp] = useState('');
  const [ogrn, setOgrn] = useState('');
  const [ogrnip, setOgrnip] = useState('');
  const [regDate, setRegDate] = useState('');
  const [npdDate, setNpdDate] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [legalAddress, setLegalAddress] = useState('');
  const [actualAddress, setActualAddress] = useState('');
  const [directorName, setDirectorName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [birthPlace, setBirthPlace] = useState('');
  const [passportSeries, setPassportSeries] = useState('');
  const [passportNumber, setPassportNumber] = useState('');
  const [passportIssuedBy, setPassportIssuedBy] = useState('');
  const [passportDate, setPassportDate] = useState('');
  const [passportCode, setPassportCode] = useState('');
  const [previousNames, setPreviousNames] = useState('');
  const [snils, setSnils] = useState('');
  const [position, setPosition] = useState('');
  const [employedByOrgId, setEmployedByOrgId] = useState('');
  const [hireDate, setHireDate] = useState('');
  const [dismissalDate, setDismissalDate] = useState('');
  const [salaryAmount, setSalaryAmount] = useState('');
  const [tabNumber, setTabNumber] = useState('');
  const [militaryLiable, setMilitaryLiable] = useState(false);
  const [bankName, setBankName] = useState('');
  const [bik, setBik] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [correspondentAccount, setCorrespondentAccount] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [files, setFiles] = useState<string[]>([]);

  // Договоры контрагента и импорт реквизитов
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [editingContract, setEditingContract] = useState<Partial<Contract> | null>(null);
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const reqFileRef = useRef<HTMLInputElement>(null);
  const loadContracts = (cpId: string | null) => {
    if (!cpId) { setContracts([]); return; }
    api.getContracts(cpId).then(setContracts).catch(() => setContracts([]));
  };
  // Удалить договор: с реализациями сервер не даст — сначала удалить их (Объекты → «Приложения и счета»)
  const removeContract = async (c: Contract) => {
    if (!(await confirmDialog(`Удалить договор ${c.number ? '№' + c.number : 'без номера'} от ${formatDay(c.date)}?`))) return;
    try { await api.deleteContract(c.id); showNotification('Договор удалён', 'success'); loadContracts(selectedId); }
    catch (e: any) { showNotification(e?.message || 'Не удалось удалить', 'error'); }
  };
  const applyRequisites = (r: Partial<Counterparty>) => {
    if (!isEditMode && r.type) setKind(r.type === CounterpartyType.ENTREPRENEUR ? 'IP' : r.type === CounterpartyType.LEGAL_ENTITY ? 'LEGAL' : kind);
    const pairs: [keyof Counterparty, (v: string) => void][] = [
      ['name', setName], ['inn', setInn], ['kpp', setKpp], ['ogrn', setOgrn], ['ogrnip', setOgrnip], ['phone', setPhone], ['email', setEmail],
      ['legalAddress', setLegalAddress], ['actualAddress', setActualAddress], ['directorName', setDirectorName], ['bankName', setBankName],
      ['bik', setBik], ['accountNumber', setAccountNumber], ['correspondentAccount', setCorrespondentAccount]];
    const filled = pairs.filter(([k]) => r[k]).map(([k, setter]) => { setter(String(r[k])); return k; });
    // ЕГРИП отдаёт дату присвоения ОГРНИП как «21.10.2013»
    const rd = (r as { registrationDate?: string }).registrationDate;
    if (rd && r.type === CounterpartyType.ENTREPRENEUR) { const m = rd.match(/^(\d{2})\.(\d{2})\.(\d{4})$/); setRegDate(m ? `${m[3]}-${m[2]}-${m[1]}` : rd); filled.push('registrationDate'); }
    showNotification(`Заполнено полей: ${filled.length}. Проверьте перед сохранением.`, 'success');
    setPasteOpen(false); setPasteText('');
  };
  const parseRequisites = async (input: { text?: string; file?: File }) => {
    try { applyRequisites(await api.parseRequisites(input)); }
    catch (e: any) { showNotification(e?.message || 'Не удалось распознать реквизиты', 'error'); }
  };
  // Заполнение по ИНН из ЕГРЮЛ/ЕГРИП (сервис ФНС): наименование, КПП, ОГРН, директор, юр. адрес
  const [egrulBusy, setEgrulBusy] = useState(false);
  const fillByInn = async () => {
    if (!/^\d{10}(\d{2})?$/.test(inn.trim())) { showNotification('Введите ИНН: 10 цифр у организации или 12 у ИП', 'error'); return; }
    setEgrulBusy(true);
    try {
      const r = await api.egrul(inn.trim());
      applyRequisites(r);
      if (r.liquidated) showNotification(`Внимание: деятельность прекращена ${r.liquidated}`, 'error');
    } catch (e: any) { showNotification(e?.message || 'ЕГРЮЛ не ответил', 'error'); }
    finally { setEgrulBusy(false); }
  };
  const EgrulButton = () => (
    <div className="flex flex-wrap items-center gap-2 -mt-1">
      <Button type="button" size="sm" variant="outline" className="h-7 text-xs" disabled={egrulBusy} onClick={fillByInn}>
        <Search className="mr-1 h-3.5 w-3.5" /> {egrulBusy ? 'Запрос в ФНС…' : 'Заполнить по ИНН (ЕГРЮЛ)'}</Button>
      <span className="text-[11px] text-slate-500">наименование, КПП, ОГРН, директор и адрес — из выписки ФНС</span>
    </div>
  );
  const fromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      if (text.trim()) { await parseRequisites({ text }); return; }
    } catch { /* буфер недоступен (http или запрет) — вставят вручную */ }
    setPasteOpen(true);
  };

  // --- Список: вкладки, поиск по названию и ИНН, фильтр по типу, сортировка ---
  const counts = useMemo(() => {
    const c: Record<Tab, number> = { cp: 0, staff: 0, se: 0, archive: 0 };
    state.counterparties.forEach(x => { const k = cpKind(x); c[x.archivedAt ? 'archive' : k === 'STAFF' ? 'staff' : k === 'SELF_EMPLOYED' ? 'se' : 'cp'] += 1; });
    return c;
  }, [state.counterparties]);
  const rows = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    const list = state.counterparties.filter(c => {
      const k = cpKind(c);
      if (!TAB_KINDS[tab].includes(k) || (kindFilter && k !== kindFilter)) return false;
      if ((tab === 'archive') !== !!c.archivedAt) return false;  // архив — отдельной вкладкой
      return !term || c.name.toLowerCase().includes(term) || (c.inn || '').includes(term) || (c.previousNames || '').toLowerCase().includes(term);
    });
    const val = (c: Counterparty): string | number => {
      const s = summary[c.id];
      switch (sort.key) {
        case 'kind': return CP_KIND_LABELS[cpKind(c)];
        case 'inn': return c.inn || '';
        case 'phone': return c.phone || c.email || '';
        case 'debt': return (s?.debtToUs || 0) - (s?.ourDebt || 0);
        case 'last': return s?.lastOperation || '';
        default: return c.name.toLowerCase();
      }
    };
    return [...list].sort((a, b) => { const x = val(a), y = val(b); return (x < y ? -1 : x > y ? 1 : 0) * sort.dir; });
  }, [state.counterparties, searchTerm, tab, kindFilter, sort, summary]);
  const sortBy = (key: SortKey) => setSort(s => ({ key, dir: s.key === key ? (s.dir === 1 ? -1 : 1) : key === 'debt' || key === 'last' ? -1 : 1 }));
  const opened = state.counterparties.find(c => c.id === openId) || null;

  const handleEdit = (c: Counterparty) => {
    setIsEditMode(true); setSelectedId(c.id); setKind(cpKind(c)); setName(c.name);
    setContractNumber(c.contractNumber || ''); setContractDate(c.contractDate || '');
    setInn(c.inn || ''); setKpp(c.kpp || ''); setOgrn(c.ogrn || ''); setOgrnip(c.ogrnip || ''); setRegDate(c.registrationDate || ''); setNpdDate(c.npdRegistrationDate || '');
    setPhone(c.phone || ''); setEmail(c.email || '');
    setLegalAddress(c.legalAddress || ''); setActualAddress(c.actualAddress || '');
    setDirectorName(c.directorName || ''); setBirthDate(c.birthDate || ''); setBirthPlace(c.birthPlace || '');
    setPassportSeries(c.passportSeries || ''); setPassportNumber(c.passportNumber || '');
    setPassportIssuedBy(c.passportIssuedBy || ''); setPassportDate(c.passportDate || ''); setPassportCode(c.passportCode || ''); setPreviousNames(c.previousNames || '');
    setSnils(c.snils || ''); setPosition(c.position || ''); setEmployedByOrgId(c.employedByOrgId || '');
    setHireDate(c.hireDate || ''); setDismissalDate(c.dismissalDate || ''); setSalaryAmount(c.salaryAmount ? String(c.salaryAmount) : '');
    setTabNumber(c.tabNumber || ''); setMilitaryLiable(!!c.militaryLiable);
    setBankName(c.bankName || ''); setBik(c.bik || ''); setAccountNumber(c.accountNumber || ''); setCorrespondentAccount(c.correspondentAccount || '');
    setCardNumber(c.cardNumber || '');
    setFiles(c.files || []);
    loadContracts(c.id);
    setIsModalOpen(true);
  };

  const handleCreate = () => { resetForm(); setKind(tab === 'archive' ? 'LEGAL' : TAB_KINDS[tab][0]); setIsModalOpen(true); };

  const isCompany = type === CounterpartyType.LEGAL_ENTITY || type === CounterpartyType.ENTREPRENEUR;
  const scopeOrg = state.currentOrganizationId && state.currentOrganizationId !== 'all' ? state.currentOrganizationId : state.organizations[0]?.id;
  const handleSubmit = async (e: React.FormEvent, thenContract = false) => {
    e.preventDefault();
    const data: Partial<Counterparty> = {
        type, name, isSelfEmployed,
        contractNumber, contractDate,
        inn, kpp, ogrn, ogrnip, npdRegistrationDate: npdDate, phone, email,
        registrationDate: type === CounterpartyType.ENTREPRENEUR ? regDate || null : undefined,
        legalAddress, actualAddress, directorName,
        birthDate, birthPlace,
        passportSeries, passportNumber, passportIssuedBy, passportDate, passportCode, previousNames: previousNames.trim() || null,
        snils, position, employedByOrgId: type === CounterpartyType.STAFF ? (employedByOrgId || null) : null,
        hireDate: type === CounterpartyType.STAFF ? hireDate || null : null, dismissalDate: type === CounterpartyType.STAFF ? dismissalDate || null : null,
        salaryAmount: type === CounterpartyType.STAFF && salaryAmount ? Number(salaryAmount) : null,
        ...(type === CounterpartyType.STAFF ? { tabNumber: tabNumber.trim() || null, militaryLiable } : {}),
        bankName, bik, accountNumber, correspondentAccount, cardNumber: cardNumber || null,
        files
    };
    if (isEditMode && selectedId) updateCounterparty(selectedId, data);
    else {
      const created = await addCounterparty(data as any);
      if (created && thenContract) {
        setIsModalOpen(false);
        if (isSelfEmployed) { setSeContractFor(created); setOpenId(created.id); resetForm(); return; }
        // Сразу договор с новым контрагентом
        setIsEditMode(true); setSelectedId(created.id); loadContracts(created.id); setIsModalOpen(true);
        setEditingContract({ counterpartyId: created.id, organizationId: scopeOrg });
        return;
      }
    }
    setIsModalOpen(false); resetForm();
  };

  const resetForm = () => {
    setIsEditMode(false); setSelectedId(null); setName('');
    setContractNumber(''); setContractDate('');
    setInn(''); setKpp(''); setOgrn(''); setOgrnip(''); setRegDate(''); setNpdDate('');
    setPhone(''); setEmail(''); setLegalAddress(''); setActualAddress(''); setDirectorName('');
    setBirthDate(''); setBirthPlace(''); setPassportSeries(''); setPassportNumber(''); setPassportIssuedBy(''); setPassportDate(''); setPassportCode(''); setPreviousNames('');
    setSnils(''); setPosition(''); setEmployedByOrgId(''); setHireDate(''); setDismissalDate(''); setSalaryAmount(''); setTabNumber(''); setMilitaryLiable(false);
    setBankName(''); setBik(''); setAccountNumber(''); setCorrespondentAccount(''); setCardNumber('');
    setFiles([]); setKind('LEGAL'); setContracts([]); setPasteOpen(false); setPasteText('');
  };

  const handleUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0] && selectedId) {
          try {
              await api.uploadCounterpartyScan(selectedId, e.target.files[0]);
              showNotification('Файл загружен', 'success');
              refreshData();
              setIsModalOpen(false);
          } catch (e) { showNotification('Ошибка загрузки', 'error'); }
      }
  };

  const Th: React.FC<{ k: SortKey; children: React.ReactNode; right?: boolean }> = ({ k, children, right }) => (
    <th className={`px-3 py-2 font-medium ${right ? 'text-right' : 'text-left'}`}>
      <button className={`inline-flex items-center gap-1 hover:text-slate-900 ${sort.key === k ? 'text-slate-900' : ''}`} onClick={() => sortBy(k)}>
        {children}<ArrowUpDown size={11} className={sort.key === k ? 'opacity-100' : 'opacity-30'} /></button>
    </th>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap justify-between items-center gap-2">
        <h1 className="text-2xl font-bold text-slate-900">Справочники</h1>
        <div className="flex flex-wrap gap-2">
          {tab === 'se' && <>
            <Button variant="outline" onClick={() => setEdoModal('contracts')}><FileSignature className="mr-2 h-4 w-4" /> Новые договоры (ЭДО)</Button>
            <Button variant="outline" onClick={() => setEdoModal('archive')}><FileArchive className="mr-2 h-4 w-4" /> Подписанные документы</Button>
          </>}
          <Button onClick={handleCreate}><Plus className="mr-2 h-4 w-4" /> Добавить</Button>
        </div>
      </div>
      {edoModal === 'contracts' && <EdoNewContractsModal onClose={() => setEdoModal(null)} />}
      {edoModal === 'archive' && <EdoArchiveModal onClose={() => setEdoModal(null)} />}
      {tab === 'se' && <CrmNewEmployees onCreated={id => setOpenId(id)} />}

      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        {([['cp', 'Контрагенты'], ['staff', 'Сотрудники'], ['se', 'Самозанятые'], ['archive', 'Архив']] as [Tab, string][]).map(([k, l]) => (
          <button key={k} onClick={() => { setTab(k); setKindFilter(''); }}
            className={`px-3 py-2 -mb-px border-b-2 text-sm whitespace-nowrap ${tab === k ? 'border-blue-600 text-blue-700 font-semibold' : 'border-transparent text-slate-500 hover:text-slate-800'}`}>
            {l} <span className="text-xs text-slate-400">{counts[k]}</span></button>
        ))}
      </div>

      <div className="bg-white p-3 rounded-lg shadow-sm border border-slate-200 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 h-4 w-4" />
          <input className="w-full rounded-md border border-slate-300 pl-9 pr-3 py-2 text-sm" placeholder="Поиск по названию или ИНН" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
        </div>
        {tab === 'cp' && <div className="sm:w-48"><Select value={kindFilter} onChange={e => setKindFilter(e.target.value as CpKind | '')}
          options={[{ value: '', label: 'Все типы' }, ...TAB_KINDS.cp.map(k => ({ value: k, label: CP_KIND_LABELS[k] }))]} /></div>}
      </div>

      {/* Десктоп: таблица */}
      <div className="hidden md:block bg-white rounded-lg border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs"><tr>
            <Th k="name">Наименование</Th><Th k="kind">Тип</Th><Th k="inn">ИНН</Th><Th k="phone">Телефон / контакт</Th>
            <Th k="debt" right>Долг нам / наш долг</Th><Th k="last" right>Последняя операция</Th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {rows.length === 0 && <tr><td colSpan={6} className="text-center text-slate-400 py-6">Ничего не найдено</td></tr>}
            {rows.map(c => {
              const k = cpKind(c);
              return (
                <tr key={c.id} className={`cursor-pointer hover:bg-slate-50 ${openId === c.id ? 'bg-blue-50/50' : ''}`} onClick={() => setOpenId(c.id)}>
                  <td className="px-3 py-2 font-medium text-slate-900 max-w-[320px] break-words">{c.name}
                    {k === 'STAFF' && c.position && <div className="text-[11px] text-slate-400 font-normal">{c.position}{c.dismissalDate ? ` · уволен ${formatDay(c.dismissalDate)}` : ''}</div>}
                    {k === 'SELF_EMPLOYED' && c.npdStatus !== 'ACTIVE' && <div className="text-[11px] text-red-600 font-normal">статус НПД не подтверждён</div>}
                    {c.reviewNote && <div className="text-[11px] text-amber-700 font-normal break-words">проверить: {c.reviewNote}</div>}
                  </td>
                  <td className="px-3 py-2"><Badge variant={KIND_BADGE[k]}>{CP_KIND_LABELS[k]}</Badge></td>
                  <td className="px-3 py-2 font-mono text-xs whitespace-nowrap">{c.inn || '—'}</td>
                  <td className="px-3 py-2 text-xs break-words">{c.phone || c.email || <span className="text-slate-300">—</span>}{c.phone && c.email && <div className="text-slate-400">{c.email}</div>}</td>
                  <td className="px-3 py-2 text-right"><DebtCell s={summary[c.id]} /></td>
                  <td className="px-3 py-2 text-right text-xs whitespace-nowrap text-slate-500">{summary[c.id]?.lastOperation ? formatDay(summary[c.id].lastOperation) : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Мобильный: компактный список */}
      <div className="md:hidden bg-white rounded-lg border border-slate-200 divide-y divide-slate-100">
        {rows.length === 0 && <div className="text-center text-slate-400 py-6 text-sm">Ничего не найдено</div>}
        {rows.map(c => (
          <button key={c.id} className="w-full flex items-center gap-2 px-3 py-2 text-left active:bg-slate-50" onClick={() => setOpenId(c.id)}>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-900 break-words">{c.name}</div>
              <div className="text-[11px] text-slate-500">{CP_KIND_LABELS[cpKind(c)]}{c.inn ? ` · ИНН ${c.inn}` : ''}</div>
            </div>
            <DebtCell s={summary[c.id]} />
            <ChevronRight size={14} className="text-slate-300 shrink-0" />
          </button>
        ))}
      </div>

      {/* Карточка: боковая панель (на мобильном — на весь экран) */}
      {opened && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={() => setOpenId(null)} />
          <aside className="relative h-full w-full sm:max-w-xl bg-slate-50 shadow-2xl overflow-y-auto">
            <CounterpartyPanel cp={opened} summary={summary[opened.id]} onClose={() => setOpenId(null)}
              onEdit={() => handleEdit(opened)} onDelete={() => { deleteCounterparty(opened.id); setOpenId(null); }}
              onContract={c => setEditingContract(c)} />
          </aside>
        </div>
      )}

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={isEditMode ? 'Редактирование' : 'Новый участник'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Select
              label="Тип"
              value={kind}
              onChange={(e) => setKind(e.target.value as CpKind)}
              disabled={isEditMode && (kind === 'LEGAL' || kind === 'IP' || kind === 'STAFF')}
              options={(isEditMode && (kind === 'PERSON' || kind === 'SELF_EMPLOYED') ? ['PERSON', 'SELF_EMPLOYED'] as CpKind[] : (Object.keys(CP_KIND_LABELS) as CpKind[]))
                  .map(k => ({ value: k, label: { LEGAL: 'Юридическое лицо (ООО)', IP: 'Индивидуальный предприниматель (ИП)', PERSON: 'Физическое лицо',
                                                  SELF_EMPLOYED: 'Самозанятый (НПД)', STAFF: 'Сотрудник в штате' }[k] }))}
          />

          {(isCompany || !isEditMode) && type !== CounterpartyType.STAFF && (
              <div className="rounded border border-blue-200 bg-blue-50 p-2 space-y-2">
                  <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className="font-semibold text-blue-800">Заполнить реквизиты:</span>
                      <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={fromClipboard}><ClipboardPaste className="mr-1 h-3.5 w-3.5" /> Из буфера обмена</Button>
                      <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => reqFileRef.current?.click()}><Upload className="mr-1 h-3.5 w-3.5" /> Из файла</Button>
                      <button type="button" className="text-blue-700 hover:underline" onClick={() => setPasteOpen(o => !o)}>вставить текст</button>
                      <input type="file" ref={reqFileRef} className="hidden" accept=".txt,.docx,.pdf,.xlsx"
                          onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) parseRequisites({ file: f }); }} />
                  </div>
                  {pasteOpen && (
                      <div className="space-y-1">
                          <textarea rows={6} value={pasteText} onChange={e => setPasteText(e.target.value)} autoFocus
                              placeholder="Вставьте карточку предприятия: наименование, ИНН, КПП, ОГРН, адрес, банк, БИК, р/с, к/с…"
                              className="w-full rounded-md border border-slate-300 px-2 py-1 text-xs font-mono" />
                          <Button type="button" size="sm" onClick={() => parseRequisites({ text: pasteText })} disabled={!pasteText.trim()}>Распознать</Button>
                      </div>
                  )}
                  <p className="text-[11px] text-blue-800/70">Карточка предприятия из письма, сайта или файла (.docx, .pdf, .xlsx, .txt). Поля заполнятся — проверьте и сохраните.</p>
              </div>
          )}

          <Input label={type === CounterpartyType.LEGAL_ENTITY ? "Полное наименование организации" : "Ф.И.О. (полностью)"} value={name} onChange={e => setName(e.target.value)} required />
          {type !== CounterpartyType.LEGAL_ENTITY && <Input label="Прежние ФИО (смена фамилии; через «;»)" value={previousNames}
              onChange={e => setPreviousNames(e.target.value)} placeholder="для поиска и сопоставления выписок" />}

          {isCompany ? (
          <div className="bg-yellow-50 p-3 rounded border border-yellow-100 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                  <h4 className="font-bold text-xs uppercase text-yellow-800">Договоры</h4>
                  {isEditMode && selectedId && <Button type="button" size="sm" variant="outline" className="h-7 text-xs"
                      onClick={() => setEditingContract({ counterpartyId: selectedId, organizationId: scopeOrg })}>
                      <Plus className="mr-1 h-3.5 w-3.5" /> Договор</Button>}
              </div>
              {!isEditMode && <p className="text-xs text-yellow-800/80">Договоры (на объём работ, на обслуживание…) — после сохранения: кнопка «Сохранить и создать договор».</p>}
              {isEditMode && contracts.length === 0 && <p className="text-xs text-yellow-800/80">Договоров пока нет.</p>}
              {contracts.map(c => (
                  <div key={c.id} className="flex flex-wrap items-center gap-2 text-sm bg-white rounded border border-yellow-100 px-2 py-1">
                      <span className="font-medium">{c.number ? `№${c.number}` : 'без номера'}</span>
                      <span className="text-xs text-slate-500 min-w-0 break-words">от {formatDay(c.date)} · {CONTRACT_KINDS[c.kind]} · {orgDisplayName(state.organizations.find(o => o.id === c.organizationId))}</span>
                      <span className="ml-auto flex gap-2">
                          <button type="button" title="Скачать договор (DOCX)" className="text-blue-600 hover:underline text-xs inline-flex items-center gap-0.5"
                              onClick={() => downloadContract(c).catch(e => showNotification(e?.message || 'Ошибка', 'error'))}><FileDown size={13} /> скачать</button>
                          <button type="button" className="text-slate-400 hover:text-blue-600" onClick={() => setEditingContract(c)}><Pencil size={13} /></button>
                          <button type="button" className="text-slate-400 hover:text-red-600" title="Удалить договор" onClick={() => removeContract(c)}><Trash2 size={13} /></button>
                      </span>
                  </div>
              ))}
          </div>
          ) : kind === 'PERSON' && (
          <div className="bg-yellow-50 p-3 rounded border border-yellow-100 space-y-3">
              <h4 className="font-bold text-xs uppercase text-yellow-800">Основание для работы</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input label="Номер договора" value={contractNumber} onChange={e => setContractNumber(e.target.value)} placeholder="Напр: 12/2025-К" />
                  <Input label="Дата договора" type="date" value={contractDate} onChange={e => setContractDate(e.target.value)} />
              </div>
          </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
             <Input label="Телефон" value={phone} onChange={e => setPhone(e.target.value)} />
             <Input label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                 placeholder="у самозанятого — на него приходит код подписи (ЭДО)" />
          </div>

          {type === CounterpartyType.LEGAL_ENTITY && (
              <div className="bg-slate-50 p-3 rounded border border-slate-200 space-y-3">
                  <h4 className="font-bold text-xs uppercase text-slate-500">Реквизиты компании</h4>
                  <Input label="Юридический адрес (по ЕГРЮЛ)" value={legalAddress} onChange={e => setLegalAddress(e.target.value)} />
                  <EgrulButton />
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <Input label="ИНН" value={inn} onChange={e => setInn(e.target.value)} />
                      <Input label="КПП" value={kpp} onChange={e => setKpp(e.target.value)} />
                      <Input label="ОГРН" value={ogrn} onChange={e => setOgrn(e.target.value)} />
                  </div>
                  <Input label="ФИО Ген. Директора" value={directorName} onChange={e => setDirectorName(e.target.value)} />
              </div>
          )}

          {type === CounterpartyType.ENTREPRENEUR && (
              <div className="bg-slate-50 p-3 rounded border border-slate-200 space-y-3">
                  <h4 className="font-bold text-xs uppercase text-slate-500">Данные ИП</h4>
                  <Input label="Адрес регистрации (по паспорту)" value={legalAddress} onChange={e => setLegalAddress(e.target.value)} />
                  <EgrulButton />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Input label="ИНН" value={inn} onChange={e => setInn(e.target.value)} />
                      <Input label="ОГРНИП" value={ogrnip} onChange={e => setOgrnip(e.target.value)} />
                      <Input label="Дата присвоения ОГРНИП" type="date" value={regDate} onChange={e => setRegDate(e.target.value)}
                          title="Нужна для XML счёта-фактуры / УПД (ДатаОГРНИП покупателя)" />
                  </div>
              </div>
          )}

          {type === CounterpartyType.INDIVIDUAL && (
              <div className="bg-slate-50 p-3 rounded border border-slate-200 space-y-3">
                  <h4 className="font-bold text-xs uppercase text-slate-500">{isSelfEmployed ? 'Данные самозанятого' : 'Данные физлица'}</h4>
                  <Input label="Адрес регистрации" value={legalAddress} onChange={e => setLegalAddress(e.target.value)} />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Input label="ИНН (12 цифр)" value={inn} onChange={e => setInn(e.target.value)} />
                      {isSelfEmployed && <Input label="Дата постановки на НПД" type="date" value={npdDate} onChange={e => setNpdDate(e.target.value)} />}
                  </div>
                  {isSelfEmployed && <p className="text-[11px] text-slate-500">Статус НПД проверяется в ФНС по ИНН — в карточке, при создании договора и перед каждой выплатой.</p>}
              </div>
          )}

          {(type === CounterpartyType.INDIVIDUAL || type === CounterpartyType.STAFF || type === CounterpartyType.ENTREPRENEUR) && (
              <div className="bg-slate-50 p-3 rounded border border-slate-200 space-y-3">
                  <h4 className="font-bold text-xs uppercase text-slate-500">Паспортные данные</h4>
                  <div className="grid grid-cols-3 gap-3">
                      <Input label="Серия" value={passportSeries} onChange={e => setPassportSeries(e.target.value)} />
                      <Input label="Номер" value={passportNumber} onChange={e => setPassportNumber(e.target.value)} />
                      <Input label="Код подр." value={passportCode} onChange={e => setPassportCode(e.target.value)} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Input label="Кем выдан" value={passportIssuedBy} onChange={e => setPassportIssuedBy(e.target.value)} />
                      <Input label="Дата выдачи" type="date" value={passportDate} onChange={e => setPassportDate(e.target.value)} />
                  </div>
                  {type === CounterpartyType.STAFF && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-slate-200">
                          <Input label="Дата рождения" type="date" value={birthDate} onChange={e => setBirthDate(e.target.value)} />
                          <Input label="Место рождения" value={birthPlace} onChange={e => setBirthPlace(e.target.value)} />
                      </div>
                  )}
              </div>
          )}

          {type === CounterpartyType.STAFF && (
              <div className="bg-blue-50 p-3 rounded border border-blue-100 space-y-3">
                  <h4 className="font-bold text-xs uppercase text-blue-800">Кадровые данные</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <Input label="ИНН (Физлица)" value={inn} onChange={e => setInn(e.target.value)} />
                      <Input label="СНИЛС" value={snils} onChange={e => setSnils(e.target.value)} />
                  </div>
                  <Input label="Должность" value={position} onChange={e => setPosition(e.target.value)} />
                  <Select label="Официально оформлен в" value={employedByOrgId} onChange={e => setEmployedByOrgId(e.target.value)} options={[
                      { value: '', label: 'Не оформлен (только неофициальные выплаты)' },
                      ...state.organizations.map(o => ({ value: o.id, label: orgDisplayName(o) }))]} />
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <Input label="Дата приёма" type="date" value={hireDate} onChange={e => setHireDate(e.target.value)} />
                      <Input label="Дата увольнения" type="date" value={dismissalDate} onChange={e => setDismissalDate(e.target.value)} />
                      <Input label="Оклад (начислено), ₽" type="number" value={salaryAmount} onChange={e => setSalaryAmount(e.target.value)} />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
                      <Input label="Табельный номер" value={tabNumber} onChange={e => setTabNumber(e.target.value)} placeholder="00004" />
                      <label className="flex items-center gap-2 text-xs pb-2"><input type="checkbox" checked={militaryLiable} onChange={e => setMilitaryLiable(e.target.checked)} /> Военнообязанный</label>
                  </div>
                  <p className="text-[11px] text-blue-800/70">Официальную зарплату с налогами проводит только эта организация; неофициальную — любая.</p>
              </div>
          )}

          <Card title="Реквизиты для оплаты" className="bg-white">
             {!isCompany && <Input label="Номер карты" value={cardNumber} onChange={e => setCardNumber(e.target.value)} placeholder="2202 …" />}
             <Input label="Наименование Банка" value={bankName} onChange={e => setBankName(e.target.value)} placeholder="ПАО Сбербанк" />
             <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
                 <Input label="БИК" value={bik} onChange={e => setBik(e.target.value)} />
                 <Input label="Расчетный счет" value={accountNumber} onChange={e => setAccountNumber(e.target.value)} />
                 <Input label="Корр. счет" value={correspondentAccount} onChange={e => setCorrespondentAccount(e.target.value)} />
             </div>
          </Card>

          {isEditMode && (
              <div className="flex gap-2 mt-4 border-t pt-4">
                  <input type="file" ref={fileInputRef} className="hidden" onChange={handleUpload} />
                  <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}><Upload className="mr-2 h-4 w-4"/> Загрузить скан</Button>
              </div>
          )}

          <div className="flex flex-wrap justify-end gap-2 pt-4">
            <Button type="button" variant="secondary" onClick={() => setIsModalOpen(false)}>Отмена</Button>
            {!isEditMode && (isCompany || isSelfEmployed) && <Button type="button" variant="outline" onClick={e => handleSubmit(e as any, true)}>
              {isSelfEmployed ? 'Сохранить и сформировать договор' : 'Сохранить и создать договор'}</Button>}
            <Button type="submit">Сохранить</Button>
          </div>
        </form>
      </Modal>
      <ContractForm item={editingContract} onClose={() => setEditingContract(null)} onSaved={() => loadContracts(selectedId)} />
      {seContractFor && <SEContractForm cp={seContractFor} onClose={() => setSeContractFor(null)} onSaved={() => refreshData()} />}
    </div>
  );
};

// --- Карточка контрагента в боковой панели ---
const CounterpartyPanel: React.FC<{ cp: Counterparty; summary?: Summary[string]; onClose: () => void; onEdit: () => void; onDelete: () => void;
                                    onContract: (c: Partial<Contract>) => void }> = ({ cp, summary, onClose, onEdit, onDelete, onContract }) => {
  const { state, showNotification, revision, patchCounterparty, refreshData } = useData();
  const k = cpKind(cp);
  const roles = [cp.isCustomer && 'покупатель', cp.isSupplier && 'поставщик / исполнитель'].filter(Boolean).join(', ');
  const reviewed = async () => {
    try { await api.clearCounterpartyReview(cp.id); patchCounterparty(cp.id, { reviewNote: null }); } catch (e: any) { showNotification(e?.message || 'Не удалось', 'error'); }
  };
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [status, setStatus] = useState<SelfEmployedStatus | null>(null);
  const restore = async () => {
    try { await api.restoreCounterparty(cp.id); patchCounterparty(cp.id, { archivedAt: null }); showNotification('Возвращено из архива', 'success'); }
    catch (e: any) { showNotification(e?.message || 'Не удалось', 'error'); }
  };
  const removeForever = async () => {
    if (!(await confirmDialog(`Удалить «${cp.name}» совсем? Это возможно, только если по нему нет операций и документов.`))) return;
    try { await api.deleteCounterparty(cp.id, true); showNotification('Удалено', 'success'); onClose(); refreshData(); }
    catch (e: any) { showNotification(e?.message || 'Не удалось', 'error'); }
  };
  const onDeleteContract = async (c: Contract) => {
    if (!(await confirmDialog(`Удалить договор ${c.number ? '№' + c.number : 'без номера'} от ${formatDay(c.date)}?`))) return;
    try { await api.deleteContract(c.id); showNotification('Договор удалён', 'success'); setContracts(cs => cs.filter(x => x.id !== c.id)); }
    catch (e: any) { showNotification(e?.message || 'Не удалось удалить', 'error'); }
  };
  useEffect(() => {
    if (k === 'LEGAL' || k === 'IP') api.getContracts(cp.id).then(setContracts).catch(() => setContracts([]));
    setStatus(null);
  }, [cp.id, k, revision]);
  const row = (label: string, value?: React.ReactNode) => value ? <div className="flex gap-2 text-xs"><span className="w-28 shrink-0 text-slate-500">{label}</span><span className="min-w-0 break-words">{value}</span></div> : null;
  const org = state.organizations.find(o => o.id === cp.employedByOrgId);
  const scopeOrg = state.currentOrganizationId && state.currentOrganizationId !== 'all' ? state.currentOrganizationId : state.organizations[0]?.id;
  return (
    <div className="min-h-full">
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-3 flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <Badge variant={KIND_BADGE[k]}>{CP_KIND_LABELS[k]}</Badge>
          <h2 className="text-lg font-bold text-slate-900 break-words mt-1">{cp.name}</h2>
          <div className="text-xs text-slate-500">{cp.inn ? `ИНН ${cp.inn}` : 'ИНН не указан'}{k === 'STAFF' && cp.position ? ` · ${cp.position}` : ''}
            {roles && ` · ${roles}`}</div>
        </div>
        <button className="p-1.5 text-slate-500 hover:text-blue-600" title="Изменить" onClick={onEdit}><Pencil size={16} /></button>
        {cp.archivedAt ? <>
          <Button size="sm" variant="outline" onClick={restore}>Вернуть из архива</Button>
          <button className="p-1.5 text-slate-500 hover:text-red-600" title="Удалить совсем (только без операций и документов)" onClick={removeForever}><Trash2 size={16} /></button>
        </> : <Button size="sm" variant="outline" title="Удалить из рабочих списков: карточка, операции и документы сохранятся во вкладке «Архив»"
                onClick={onDelete}><Archive className="mr-1 h-3.5 w-3.5" /> В архив</Button>}
        <button className="p-1.5 text-slate-500 hover:text-slate-900" title="Закрыть" onClick={onClose}><X size={18} /></button>
      </div>
      <div className="p-4 space-y-3">
        {cp.reviewNote && (
          <div className="rounded border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900 flex gap-2 items-start">
            <span className="min-w-0 flex-1 break-words">Проверить (перенос данных): {cp.reviewNote}</span>
            <button className="text-blue-700 hover:underline shrink-0" onClick={reviewed}>Проверено</button>
          </div>
        )}
        {summary && (summary.debtToUs > 0.004 || summary.ourDebt > 0.004 || summary.lastOperation) && (
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="rounded bg-white border border-slate-200 p-2"><div className="text-[10px] text-slate-500">Долг нам</div><div className={`text-sm font-bold whitespace-nowrap ${summary.debtToUs > 0.004 ? 'text-red-600' : 'text-slate-400'}`}>{formatMoney(summary.debtToUs)}</div></div>
            <div className="rounded bg-white border border-slate-200 p-2"><div className="text-[10px] text-slate-500">Наш долг</div><div className={`text-sm font-bold whitespace-nowrap ${summary.ourDebt > 0.004 ? 'text-amber-700' : 'text-slate-400'}`}>{formatMoney(summary.ourDebt)}</div></div>
            <div className="rounded bg-white border border-slate-200 p-2"><div className="text-[10px] text-slate-500">Последняя операция</div><div className="text-sm font-bold">{summary.lastOperation ? formatDay(summary.lastOperation) : '—'}</div></div>
          </div>
        )}
        <Card className="bg-white">
          <div className="space-y-1">
            {row('Телефон', cp.phone)}{row('Email', cp.email)}
            {row('Адрес', cp.legalAddress || cp.actualAddress)}
            {row('КПП / ОГРН', [cp.kpp, cp.ogrn || cp.ogrnip].filter(Boolean).join(' / '))}
            {row('Директор', cp.directorName)}
            {row('Паспорт', [cp.passportSeries, cp.passportNumber].filter(Boolean).join(' '))}
            {row('Прежние ФИО', cp.previousNames)}
            {row('Оформлен в', k === 'STAFF' ? (org ? orgDisplayName(org) : 'не оформлен официально') : undefined)}
            {row('Приём / увольнение', k === 'STAFF' && (cp.hireDate || cp.dismissalDate) ? `${formatDay(cp.hireDate) || '—'} / ${formatDay(cp.dismissalDate) || '—'}` : undefined)}
            {row('Оклад', cp.salaryAmount ? formatMoney(cp.salaryAmount) : undefined)}
            {row('Карта', cp.cardNumber)}
            {row('Счёт', cp.accountNumber ? `${cp.accountNumber}${cp.bankName ? `, ${cp.bankName}` : ''}${cp.bik ? `, БИК ${cp.bik}` : ''}` : undefined)}
            {cp.files?.length > 0 && <div className="text-xs text-blue-600 flex items-center gap-1"><Paperclip size={12} /> {cp.files.length} файлов</div>}
          </div>
        </Card>
        {(k === 'LEGAL' || k === 'IP') && (
          <Card className="bg-white">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-bold text-xs uppercase text-slate-500">Договоры</h4>
              <Button size="sm" variant="outline" onClick={() => onContract({ counterpartyId: cp.id, organizationId: scopeOrg })}><Plus className="mr-1 h-3.5 w-3.5" /> Договор</Button>
            </div>
            {contracts.length === 0 && <p className="text-xs text-slate-400">Договоров пока нет.</p>}
            {contracts.map(c => (
              <div key={c.id} className="flex flex-wrap items-center gap-2 text-sm py-1 border-t border-slate-100">
                <span className="font-medium">{c.number ? `№${c.number}` : 'без номера'}</span>
                <span className="text-xs text-slate-500 min-w-0 break-words">от {formatDay(c.date)} · {CONTRACT_KINDS[c.kind]} · {orgDisplayName(state.organizations.find(o => o.id === c.organizationId))}</span>
                <span className="ml-auto flex gap-2">
                  <button className="text-blue-600 hover:underline text-xs inline-flex items-center gap-0.5" onClick={() => downloadContract(c).catch(e => showNotification(e?.message || 'Ошибка', 'error'))}><FileDown size={13} /> скачать</button>
                  <button className="text-slate-400 hover:text-blue-600" onClick={() => onContract(c)}><Pencil size={13} /></button>
                  <button className="text-slate-400 hover:text-red-600" title="Удалить договор" onClick={() => onDeleteContract(c)}><Trash2 size={13} /></button>
                </span>
              </div>
            ))}
          </Card>
        )}
        {k === 'SELF_EMPLOYED' && (
          <>
            <NpdStatus cp={cp} status={status} onChecked={setStatus} />
            <Card className="bg-white"><SelfEmployedWorks cp={cp} onStatus={setStatus} /></Card>
          </>
        )}
        {/* ИП-исполнитель (монтажник с ИП): те же договор → задание → акт → выплата, документ — акт / счёт */}
        {k === 'IP' && (cp.inn || '').length === 12 && <Card className="bg-white"><SelfEmployedWorks cp={cp} /></Card>}
        {k === 'STAFF' && <Card className="bg-white"><StaffSections cp={cp} /></Card>}
      </div>
    </div>
  );
};
